import { useState } from 'react'
import { cn } from '@/lib/utils'
import { gappedLinePath } from '@/lib/chart-path'
import { naNumber } from '@/lib/deep-dive-adapters'
import type { Freshness, TimelinePoint } from '@/lib/system-intelligence'
import { FreshnessNote, PanelError, PanelLoading } from './PanelState'

const W = 720
const H = 200
const PAD = 14

/** How many labels the axis carries at most, ends included. */
const MAX_TICKS = 5

/**
 * Evenly spaced points to label, always including the first and last.
 *
 * Derived from the series rather than hardcoded to the 24 hours of a day: the
 * same chart now plots a 7-day window, where a fixed hour axis would have
 * labelled seven days with five clock times.
 */
function axisTicks(points: TimelinePoint[]): TimelinePoint[] {
  if (points.length <= MAX_TICKS) return points
  const step = (points.length - 1) / (MAX_TICKS - 1)
  return Array.from({ length: MAX_TICKS }, (_, i) => points[Math.round(i * step)])
}

interface SalesTimelineProps {
  data: TimelinePoint[]
  /** CURR.HOUR and AVG HOUR, straight off the payload — null when unknown. */
  headline: { currentHourSales: number | null; baselineThisHour: number | null }
  /** The business day and its timezone, e.g. "2026-09-04" / "America/Phoenix". */
  date?: string
  timezone?: string
  /** How many weekdays the baseline averages over, for the caption. */
  baselineDays?: number
  /** False once a past day is selected, so the title stops claiming "Today". */
  isToday?: boolean
  /** What one point covers — "this hour" for a day, "this day" for a week. */
  unitWord?: string
  /** Overrides the panel heading when the chart is not showing a single day. */
  title?: string
  /**
   * A caption under the heading — used for "Data since <date>" when the source
   * starts inside the chosen period, so a short line is read as coverage rather
   * than as a quiet stretch.
   */
  note?: string
  /** Names the two headline figures — they mean different things per span. */
  headlineLabels?: { current: string; baseline: string }
  freshness?: Freshness
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  /** Tighter panel, for a page tuned to fit the viewport. */
  compact?: boolean
}

/**
 * Today's sales against the trailing weekday baseline, hour by hour.
 *
 * The plotted series are the *cumulative* totals, per the contract — a running
 * total against a running baseline shows the day falling behind and staying
 * behind, where the per-hour counts just look noisy. Both share one scale, since
 * the gap between them is the whole point of the panel. Straight segments, not a
 * spline: these are hourly counts, and smoothing would invent movement between
 * hours that never happened. Hovering reads out the hour under the cursor,
 * including the per-hour figures the lines themselves don't show.
 */
export function SalesTimeline({
  data,
  headline,
  date,
  timezone,
  baselineDays,
  isToday = true,
  unitWord = 'this hour',
  title,
  headlineLabels = { current: 'Curr.Hour', baseline: 'Avg Hour' },
  note,
  freshness,
  isLoading,
  error,
  onRetry,
  compact,
}: SalesTimelineProps) {
  /** Index under the cursor; null when the pointer is off the plot. */
  const [hovered, setHovered] = useState<number | null>(null)

  const values = data
    .flatMap((d) => [d.cumulativeActual, d.cumulativeBaseline])
    .filter((n): n is number => typeof n === 'number')
  // Both series start at (or near) zero, so the floor is pinned there rather
  // than at the series minimum — a chart that started at the first hour's value
  // would exaggerate a quiet morning into a cliff.
  const max = Math.max(...values, 1)
  const spread = max || 1

  const x = (i: number) => (i / Math.max(data.length - 1, 1)) * W
  const y = (v: number) => H - PAD - (v / spread) * (H - 2 * PAD)

  /**
   * A null value yields a null point, which `gappedLinePath` turns into a break
   * in the line. Projecting it as 0 would draw a measured floor across buckets
   * nobody has data for.
   */
  const project = (pick: (d: TimelinePoint) => number | null) =>
    data.map((d, i) => {
      const v = pick(d)
      return typeof v === 'number' ? { x: x(i), y: y(v) } : null
    })

  const actual = project((d) => d.cumulativeActual)
  const baseline = project((d) => d.cumulativeBaseline)
  /** Where the line actually is, for the crosshair and the readout card. */
  const anchorAt = (i: number) => actual[i] ?? { x: x(i), y: H - PAD }

  /**
   * Nearest hour to the cursor, measured against the container's width rather
   * than the viewBox — `preserveAspectRatio="none"` means the two don't share a
   * scale.
   */
  const trackCursor = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, width } = e.currentTarget.getBoundingClientRect()
    if (!width || data.length < 2) return
    const index = Math.round(((e.clientX - left) / width) * (data.length - 1))
    setHovered(Math.min(Math.max(index, 0), data.length - 1))
  }

  const point = hovered === null ? null : data[hovered]

  return (
    <section
      className={cn(
        'border-line bg-surface flex h-full flex-col rounded-lg border border-dashed',
        compact ? 'p-4' : 'p-5',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
            {/* Naming the day rather than saying "Today" when it is not: the
                panel's whole claim is a comparison, and a mislabelled left-hand
                side makes the comparison wrong rather than merely unclear. */}
            {title ??
              `Sales Intelligence Timeline (${isToday ? 'Today' : (date ?? 'Selected day')} vs 30D Avg)`}
          </h2>
          {note && (
            <p className="text-degraded mt-1 font-mono text-[10px] tracking-[0.06em] uppercase">
              {note}
            </p>
          )}
          {date && (
            <p className="text-fg-subtle mt-1 font-mono text-[10px] tracking-[0.06em]">
              {date}
              {timezone ? ` · ${timezone}` : ''}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-6">
          <Headline
            value={headline.currentHourSales}
            label={headlineLabels.current}
            tone="text-primary-bright"
          />
          <Headline
            value={headline.baselineThisHour}
            label={headlineLabels.baseline}
            tone="text-fg-muted"
          />
        </div>
      </div>

      {isLoading ? (
        <PanelLoading className="flex-1" />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} className="flex-1" />
      ) : data.length === 0 ? (
        <PanelLoading label="No hours reported" className="flex-1" />
      ) : (
        <>
          <div
            className={cn(
              'relative mt-4 cursor-crosshair',
              compact ? 'h-[118px]' : 'min-h-[150px] flex-1',
            )}
            onMouseMove={trackCursor}
            onMouseLeave={() => setHovered(null)}
          >
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              className="h-full w-full"
              fill="none"
              role="img"
              aria-label={`Actual sales through the day against the weekday baseline, ${naNumber(headline.currentHourSales)} this hour against an hourly average of ${naNumber(headline.baselineThisHour)}`}
            >
              <path
                d={gappedLinePath(baseline)}
                stroke="var(--color-fg-subtle)"
                strokeWidth="2"
                strokeDasharray="7 5"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={gappedLinePath(actual)}
                stroke="var(--color-primary-bright)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* Crosshair and readout are HTML, not SVG: the stretched viewBox
                would squash a circle into an ellipse and skew the text. */}
            {hovered !== null && point && (
              <>
                <span
                  className="bg-line-bright pointer-events-none absolute inset-y-0 w-px"
                  style={{ left: `${(anchorAt(hovered).x / W) * 100}%` }}
                />
                <div
                  className="border-line bg-surface-2 text-fg pointer-events-none absolute top-0 z-10 rounded-lg border px-2.5 py-1.5 font-mono text-[10px] whitespace-nowrap"
                  style={{
                    left: `${(anchorAt(hovered).x / W) * 100}%`,
                    // Keep the card inside the plot at both ends of the series.
                    transform:
                      hovered === 0
                        ? 'translateX(0)'
                        : hovered === data.length - 1
                          ? 'translateX(-100%)'
                          : 'translateX(-50%)',
                  }}
                >
                  <p className="text-fg-subtle tracking-[0.08em] uppercase">{point.label}</p>
                  {point.actual === null ? (
                    // Unknown, not zero — the one thing this card must not do is
                    // let a gap read as a quiet period.
                    <p className="text-fg-muted mt-0.5">No data for this bucket</p>
                  ) : (
                    <>
                      <p className="text-primary-bright mt-0.5">
                        {naNumber(point.cumulativeActual)} cumulative · {point.actual} {unitWord}
                      </p>
                      <p className="text-fg-muted">
                        {point.cumulativeBaseline} baseline · {point.baseline} {unitWord}
                      </p>
                    </>
                  )}
                  {point.partial && (
                    <p className="text-degraded mt-0.5">Partial — data starts inside this bucket</p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="text-fg-subtle mt-2 flex justify-between font-mono text-[11px]">
            {axisTicks(data).map((p) => (
              <span key={p.label}>{p.label}</span>
            ))}
          </div>

          <div
            className={cn('flex flex-wrap items-center gap-x-6 gap-y-2', compact ? 'mt-3' : 'mt-4')}
          >
            <Legend tone="bg-primary-bright" label="Actual sales" />
            <Legend
              tone="bg-fg-subtle"
              label={baselineDays ? `Baseline (${baselineDays} weekdays)` : 'Weekday avg baseline'}
            />
          </div>

          <FreshnessNote freshness={freshness} />
        </>
      )}
    </section>
  )
}

/** A headline figure; "n/a" rather than 0 when the payload could not report it. */
function Headline({ value, label, tone }: { value: number | null; label: string; tone: string }) {
  return (
    <p className="text-right">
      <span className={cn('block font-mono text-2xl leading-none font-bold', tone)}>
        {naNumber(value)}
      </span>
      <span className="text-fg-subtle mt-1 block font-mono text-[10px] tracking-[0.08em] uppercase">
        {label}
      </span>
    </p>
  )
}

function Legend({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="text-fg-muted flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] uppercase">
      <span className={cn('h-0.5 w-5 rounded-full', tone)} />
      {label}
    </span>
  )
}
